import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { cn } from '@/lib/utils';
import { Trash2 } from 'lucide-react';
import { Label } from '../ui/label';
import { DraperyModule } from "../../redux";

type HardwarePricingTableProps = {
  rows: any[];
  shippingRow: any;
  includeShipping: boolean;
  disabled?: boolean;
  onChange(
    index: number,
    field: 'estimateDescription' | 'cost' | 'quantity' | 'markup' | 'customerPrice',
    value: string,
    isShipping?: boolean
  ): void;
  onRemoveRow?(index: number): void;
  onToggleShipping(include: boolean): void;
  draperyOrderHardware: DraperyModule.DraperyOrderHardware;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const parseNumber = (value?: string | number) => {
  if (value === null || value === undefined || value === '') return NaN;
  const num = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : NaN;
};

const computeCalculatedPrice = (row: any, draperyOrderHardware?: any): string => {
  const quantity = parseNumber(row?.quantity);
  const cost = parseNumber(row?.cost);
  const markup = parseNumber(row?.markup) || 0;
  // additional price adjustment
  const additionalPriceAdjustment = draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType || 'none';
  // additional price adjustment value
  const additionalPriceAdjustmentValue =
    Number(draperyOrderHardware?.hardwareAdditionalPriceAdjustment) || 0;
  // additional price adjustment (discount or markup)
  const additionalPriceAdjustmentCalculate =
    additionalPriceAdjustment === 'discount'
      ? -additionalPriceAdjustmentValue
      : additionalPriceAdjustmentValue;

  if (Number.isNaN(quantity) || Number.isNaN(cost)) return '';

  // Total sum of client additional price adjustment and markup
  const totalPercentage = markup / 100 + additionalPriceAdjustmentCalculate / 100;

  const calculated = quantity * cost * (1 + totalPercentage);
  return Number.isFinite(calculated) ? calculated.toFixed(2) : '';
};

const formatCurrency = (value?: string | number) => {
  const num = parseNumber(value);
  if (Number.isNaN(num)) return '';
  return currencyFormatter.format(num);
};

const PricingCell = ({ value, disabled }: { value?: string | number; disabled?: boolean }) => (
  <div className={cn('p-3 text-center text-sm', disabled && 'text-[#7a8a96]')}>
    {value !== undefined && value !== '' ? formatCurrency(value) : '$0.00'}
  </div>
);

export const HardwarePricingTable = ({
  rows,
  shippingRow,
  includeShipping,
  disabled,
  onRemoveRow,
  onChange,
  onToggleShipping,
  draperyOrderHardware
}: HardwarePricingTableProps) => {
  const descriptionRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const adjustHeight = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    Object.values(descriptionRefs.current).forEach((node) => {
      adjustHeight(node);
    });
  }, [rows, shippingRow?.estimateDescription, adjustHeight]);

  const setDescriptionRef = (key: string) => (el: HTMLTextAreaElement | null) => {
    descriptionRefs.current[key] = el;
    adjustHeight(el);
  };

  const handleDescriptionKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    index: number,
    isShipping?: boolean
  ) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const target = e.currentTarget;
    const { selectionStart, selectionEnd, value } = target;
    const nextValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
    onChange(index, 'estimateDescription', nextValue, isShipping);
    requestAnimationFrame(() => {
      target.selectionStart = target.selectionEnd = selectionStart + 1;
      adjustHeight(target);
    });
  };

  const totals = useMemo(() => {
    const rowsToSum = includeShipping ? [...rows, shippingRow] : rows;
    return rowsToSum.reduce(
      (acc, row) => {
        const calculated = computeCalculatedPrice(row, draperyOrderHardware) || row?.calculatedPrice;
        const calculatedNumber = parseNumber(calculated);
        if (!Number.isNaN(calculatedNumber)) acc.calculated += calculatedNumber;

        const customerRaw =
          row?.customerPrice !== undefined &&
          row?.customerPrice !== null &&
          row?.customerPrice !== ''
            ? row.customerPrice
            : calculated;
        const customerNumber = parseNumber(customerRaw);
        if (!Number.isNaN(customerNumber)) acc.customer += customerNumber;

        return acc;
      },
      { calculated: 0, customer: 0 }
    );
  }, [rows, shippingRow, includeShipping]);

  const shippingDisabled = disabled || !includeShipping;

  const renderRow = (row: any, index: number, isShipping?: boolean) => {
    const calculatedPrice = computeCalculatedPrice(row, draperyOrderHardware) || row?.calculatedPrice || '';
    const customerPrice =
      row?.customerPrice !== undefined && row?.customerPrice !== null && row?.customerPrice !== ''
        ? row.customerPrice
        : calculatedPrice;
    const rowDisabled = disabled || (isShipping && !includeShipping);
    const descriptionLines =
      row?.estimateDescription && typeof row.estimateDescription === 'string'
        ? row.estimateDescription.split('\n').length || 1
        : 1;
    const initialRows = Math.max(2, descriptionLines);

    return (
      <TableRow
        key={row?.id || `pricing-row-${index}`}
        className={cn(
          'border-b border-input bg-white transition-colors',
          index % 2 === 1 && '',
          rowDisabled && 'opacity-60'
        )}
      >
        <TableCell className="align-top p-3">
          <Textarea
            value={row?.estimateDescription || ''}
            onChange={(e) => {
              onChange(index, 'estimateDescription', e.target.value, isShipping);
              adjustHeight(e.target);
            }}
            onKeyDown={(e) => handleDescriptionKeyDown(e, index, isShipping)}
            placeholder="Enter description"
            className="rounded-lg border-[#cddfe6] bg-white text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]"
            disabled={rowDisabled}
            ref={setDescriptionRef(`${isShipping ? 'shipping' : 'row'}-${index}`)}
            rows={initialRows}
            style={{ overflow: 'hidden' }}
          />
        </TableCell>
        <TableCell className="w-[90px] p-3">
          <Input
            value={row?.quantity || ''}
            onChange={(e) => onChange(index, 'quantity', e.target.value, isShipping)}
            inputMode="decimal"
            disabled={rowDisabled}
            className="h-10 rounded-lg border-[#cddfe6] bg-white text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]"
          />
        </TableCell>
        <TableCell className="w-[130px] p-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
              $
            </span>
            <Input
              value={row?.cost || ''}
              onChange={(e) => onChange(index, 'cost', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className="h-10 rounded-lg border-[#cddfe6] bg-white pl-6 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]"
            />
          </div>
        </TableCell>
        <TableCell className="w-[120px] p-3">
          <div className="relative">
            <Input
              value={row?.markup || ''}
              onChange={(e) => onChange(index, 'markup', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className="h-10 rounded-lg border-[#cddfe6] bg-white pr-7 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
              %
            </span>
          </div>
        </TableCell>
        {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' ? (
          <TableCell className="p-3 text-center text-sm">
            {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType === 'discount' && '-'}
            {draperyOrderHardware?.hardwareAdditionalPriceAdjustment || '0'}%
          </TableCell>
        ) : null}
        <TableCell className="p-3">
          <PricingCell value={calculatedPrice} disabled={rowDisabled} />
        </TableCell>
        <TableCell className="p-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
              $
            </span>
            <Input
              value={customerPrice}
              onChange={(e) => onChange(index, 'customerPrice', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className="h-10 rounded-lg border-[#cddfe6] bg-white pl-6 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]"
            />
          </div>
        </TableCell>

        {!isShipping && rows.length > 1 && onRemoveRow && (
          <TableCell className="w-[60px] align-top text-right">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              onClick={() => onRemoveRow(index)}
              disabled={disabled}
              className="text-[#28586f] hover:bg-twSecondary"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <div className="rounded-[8px] border border-[#cddfe6] bg-gradient-to-br from-[#f6fbfd] to-white overflow-hidden">
      {/* <div className="rounded-[8px] flex flex-wrap items-center justify-between gap-3 border-b border-[#cddfe6] bg-gradient-to-r from-[#d8ecf2] via-[#e4f3f7] to-[#d8ecf2] px-5 py-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-[#2f6276] tracking-[0.01em]">Hardware Pricing</p>
        </div> */}
      {/* <div className="flex items-center gap-3 rounded-full bg-white/80 px-3 py-1.5 shadow-[0_6px_18px_rgba(47,98,118,0.12)]">
          <Switch
            checked={includeShipping}
            onClick={() => !disabled && onToggleShipping(!includeShipping)}
            disabled={disabled}
            aria-label="Toggle shipping and handling for hardware"
          />
          <span
            className={cn(
              'text-[13px] font-medium',
              disabled ? 'text-muted-foreground' : 'text-[#28586f]'
            )}
          >
            Add Shipping & Handling
          </span>
        </div> */}
      {/* </div> */}

      <div className="">
        <Table className="min-w-[960px]">
          <TableHeader className="bg-teal/10">
            <TableRow className="border-input">
              <TableHead className="text-left p-3 font-medium text-sm">
                Estimate Description
              </TableHead>
              <TableHead className="text-center p-3 font-medium text-sm w-20">Quantity</TableHead>
              <TableHead className="text-center p-3 font-medium text-sm w-28">Cost</TableHead>
              <TableHead className="text-center p-3 font-medium text-sm w-24">
                Product Markup
              </TableHead>
              {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' && (
                <TableHead className="text-center p-3 font-medium text-sm w-24">
                  Client Price Adjustment
                </TableHead>
              )}
              <TableHead className="text-center p-3 font-medium text-sm w-28">
                Calculated Price
              </TableHead>
              <TableHead className="text-center p-3 font-medium text-sm w-32">
                Customer-Facing Price
              </TableHead>
              {/* <TableHead className="w-[60px] rounded-tr-[8px]" /> */}
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.map((row, index) => renderRow(row, index))}

            {includeShipping && (
              <TableRow
                className={cn(
                  'bg-white border-b border-input transition-colors',
                  shippingDisabled ? '' : ''
                )}
              >
                <TableCell className="p-3">
                  <div
                    className={cn(
                      'flex min-h-[44px] items-center rounded-lg border px-3 text-[13px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
                      shippingDisabled
                        ? 'border-[#d8dfe2] text-[#7a8a96]'
                        : 'border-[#cddfe6] text-[#2f6276]'
                    )}
                  >
                    {shippingRow?.estimateDescription || 'Shipping & Handling for Hardware'}
                  </div>
                </TableCell>
                <TableCell className="w-[90px] p-3">
                  <Input
                    value={shippingRow?.quantity || ''}
                    onChange={(e) => onChange(0, 'quantity', e.target.value, true)}
                    inputMode="decimal"
                    disabled={shippingDisabled}
                    className={cn(
                      'h-10 rounded-lg border-[#cddfe6] text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]',
                      shippingDisabled
                        ? 'border-[#d8dfe2] bg-[#f5f6f7] text-[#7a8a96]'
                        : 'bg-white text-[#1f4e62]'
                    )}
                  />
                </TableCell>
                <TableCell className="w-[130px] p-3">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
                      $
                    </span>
                    <Input
                      value={shippingRow?.cost || ''}
                      onChange={(e) => onChange(0, 'cost', e.target.value, true)}
                      inputMode="decimal"
                      placeholder="0.00"
                      disabled={shippingDisabled}
                      className={cn(
                        'h-10 rounded-lg border-[#cddfe6] pl-6 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]',
                        shippingDisabled
                          ? 'border-[#d8dfe2] bg-[#f5f6f7] text-[#7a8a96]'
                          : 'bg-white text-[#1f4e62]'
                      )}
                    />
                  </div>
                </TableCell>
                <TableCell className="w-[120px] p-3">
                  <div className="relative">
                    <Input
                      value={shippingRow?.markup || ''}
                      onChange={(e) => onChange(0, 'markup', e.target.value, true)}
                      inputMode="decimal"
                      placeholder="0.00"
                      disabled={shippingDisabled}
                      className={cn(
                        'h-10 rounded-lg border-[#cddfe6] pr-7 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]',
                        shippingDisabled
                          ? 'border-[#d8dfe2] bg-[#f5f6f7] text-[#7a8a96]'
                          : 'bg-white text-[#1f4e62]'
                      )}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
                      %
                    </span>
                  </div>
                </TableCell>
                {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' ? (
                  <TableCell className="p-3 text-center text-sm">
                    {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType === 'discount' && '-'}
                    {draperyOrderHardware?.hardwareAdditionalPriceAdjustment || '0'}%
                  </TableCell>
                ) : null}
                <TableCell className="p-3">
                  <PricingCell
                    value={
                      computeCalculatedPrice(shippingRow, draperyOrderHardware) ||
                      shippingRow?.calculatedPrice
                    }
                    disabled={shippingDisabled}
                  />
                </TableCell>
                <TableCell className="p-3">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-twBlue2">
                      $
                    </span>
                    <Input
                      value={
                        shippingRow?.customerPrice !== undefined &&
                        shippingRow?.customerPrice !== null &&
                        shippingRow?.customerPrice !== ''
                          ? shippingRow?.customerPrice
                          : computeCalculatedPrice(shippingRow, draperyOrderHardware) ||
                            shippingRow?.calculatedPrice
                      }
                      onChange={(e) => onChange(0, 'customerPrice', e.target.value, true)}
                      inputMode="decimal"
                      placeholder="0.00"
                      disabled={shippingDisabled}
                      className={cn(
                        'h-10 rounded-lg border-[#cddfe6] pl-6 text-right text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus-visible:border-[#3b97b1]',
                        shippingDisabled
                          ? 'border-[#d8dfe2] bg-[#f5f6f7] text-[#7a8a96]'
                          : 'bg-white text-[#1f4e62]'
                      )}
                    />
                  </div>
                </TableCell>
                {/* <TableCell /> */}
              </TableRow>
            )}

            <TableRow className="bg-white border-input font-semibold">
              <TableCell
                className="p-3"
                colSpan={draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' ? 3 : 2}
              >
                <div className="flex items-center gap-2">
                  <Switch
                    checked={includeShipping}
                    onClick={() => !disabled && onToggleShipping(!includeShipping)}
                    disabled={disabled}
                    aria-label="Toggle shipping and handling for hardware"
                    id="switcher"
                  />
                  <Label
                    htmlFor="switcher"
                    className={cn(
                      'text-[13px] font-medium cursor-pointer',
                      disabled ? 'text-muted-foreground' : 'text-[#28586f]'
                    )}
                  >
                    Add Shipping & Handling
                  </Label>
                </div>
              </TableCell>
              <TableCell colSpan={2} className="text-[16px] text-right">
                TOTAL PRICE
              </TableCell>
              <TableCell className="text-[16px] text-right">
                {formatCurrency(totals.calculated) || '$0.00'}
              </TableCell>
              <TableCell className="text-[16px] text-right">
                {formatCurrency(totals.customer) || '$0.00'}
              </TableCell>
              {/* <TableCell /> */}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
